import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../routing/routeConfiguration';
import {
  MOBILE_BOTTOM_NAV_MEDIA_QUERY,
  isPreviewMobileBottomNav,
  shouldShowMobileBottomNav,
  useIsInTwinrApp,
} from '../../../util/twinrApp';
import { NamedLink } from '../../../components';

import css from './MobileBottomNav.module.css';

const BODY_CLASS = 'hasMobileBottomNav';
const BODY_PREVIEW_CLASS = 'hasMobileBottomNavPreview';
// Android: no home indicator, so the extra bottom padding added for iOS is wasted space.
const BODY_ANDROID_CLASS = 'hasMobileBottomNavAndroid';

const isAndroid = () =>
  typeof navigator !== 'undefined' && /android/i.test(navigator.userAgent || '');

const HomeIcon = ({ filled }) => (
  <svg className={css.icon} viewBox="0 0 24 24" aria-hidden="true">
    {filled ? (
      <path
        fill="currentColor"
        d="M12.4 3.35a.9.9 0 0 0-1.07.02L3.7 9.4A1 1 0 0 0 3.2 10.2v9.05c0 .7.57 1.25 1.25 1.25H9.1c.47 0 .85-.38.85-.85v-4.2h4.1v4.2c0 .47.38.85.85.85h4.65c.68 0 1.25-.55 1.25-1.25V10.2a1 1 0 0 0-.5-.86l-7.63-6Z"
      />
    ) : (
      <path
        d="M4.6 10.15 12 4.4l7.4 5.75V19.1a.9.9 0 0 1-.9.9h-4.2v-5.15h-4.6V20h-4.2a.9.9 0 0 1-.9-.9V10.15Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
    )}
  </svg>
);

const PublishIcon = ({ filled }) => (
  <svg className={css.icon} viewBox="0 0 24 24" aria-hidden="true">
    {filled ? (
      <path
        fill="currentColor"
        fillRule="evenodd"
        d="M12 3.25a8.75 8.75 0 1 1 0 17.5 8.75 8.75 0 0 1 0-17.5Zm0 4.25a.9.9 0 0 0-.9.9v2.7H8.4a.9.9 0 1 0 0 1.8h2.7v2.7a.9.9 0 1 0 1.8 0v-2.7h2.7a.9.9 0 1 0 0-1.8h-2.7V8.4a.9.9 0 0 0-.9-.9Z"
      />
    ) : (
      <>
        <circle cx="12" cy="12" r="8.25" fill="none" stroke="currentColor" strokeWidth="1.7" />
        <path
          d="M12 8.25v7.5M8.25 12h7.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
      </>
    )}
  </svg>
);

const ChatIcon = ({ filled }) => (
  <svg className={css.icon} viewBox="0 0 24 24" aria-hidden="true">
    {filled ? (
      <path
        d="M12 3.5c-4.7 0-8.5 3.17-8.5 7.08 0 2.22 1.22 4.2 3.12 5.5v3.17l3.28-1.86c.67.16 1.37.25 2.1.25 4.7 0 8.5-3.17 8.5-7.06S16.7 3.5 12 3.5Z"
        fill="currentColor"
      />
    ) : (
      <path
        d="M6.4 6.35C7.7 5.2 9.72 4.5 12 4.5c4.28 0 7.75 2.84 7.75 6.33S16.28 17.16 12 17.16c-.72 0-1.42-.08-2.08-.24l-3.27 1.86V16C4.9 14.76 4.25 13.08 4.25 11.2c0-1.86.78-3.54 2.15-4.85Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinejoin="round"
      />
    )}
  </svg>
);

const ProfileIcon = ({ filled }) => (
  <svg className={css.icon} viewBox="0 0 24 24" aria-hidden="true">
    {filled ? (
      <>
        <circle cx="12" cy="8" r="3.4" fill="currentColor" />
        <path
          d="M7.22 20c-.62 0-1.1-.55-1.02-1.15C6.55 16.15 8.9 14.5 12 14.5s5.45 1.65 5.8 4.35c.08.6-.4 1.15-1.02 1.15H7.22Z"
          fill="currentColor"
        />
      </>
    ) : (
      <>
        <circle cx="12" cy="8" r="3.25" fill="none" stroke="currentColor" strokeWidth="1.7" />
        <path
          d="M5.5 19.25c.7-3.2 3.1-5 6.5-5s5.8 1.8 6.5 5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
        />
      </>
    )}
  </svg>
);

const ListingsIcon = ({ filled }) => (
  <svg className={css.icon} viewBox="0 0 24 24" aria-hidden="true">
    {filled ? (
      <>
        <rect x="4" y="4.5" width="16" height="6.2" rx="1.6" fill="currentColor" />
        <rect x="4" y="13.3" width="16" height="6.2" rx="1.6" fill="currentColor" />
      </>
    ) : (
      <>
        <rect
          x="4.35"
          y="4.85"
          width="15.3"
          height="5.5"
          rx="1.4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
        />
        <rect
          x="4.35"
          y="13.65"
          width="15.3"
          height="5.5"
          rx="1.4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
        />
      </>
    )}
  </svg>
);

const isPage = (currentPage, name) =>
  currentPage === name ||
  (typeof currentPage === 'string' && currentPage.indexOf(`${name}:`) === 0);

const useIsNarrowViewport = () => {
  const [isNarrow, setIsNarrow] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return undefined;
    }
    const mediaQuery = window.matchMedia(MOBILE_BOTTOM_NAV_MEDIA_QUERY);
    const update = () => setIsNarrow(mediaQuery.matches);
    update();
    mediaQuery.addEventListener('change', update);
    return () => mediaQuery.removeEventListener('change', update);
  }, []);

  return isNarrow;
};

/**
 * Mini bottom nav for logged-in Twinr app users.
 * @param {Object} props
 * @param {boolean} props.isAuthenticated
 * @param {string} props.inboxTab
 * @param {string?} props.createListingType
 * @param {number} props.notificationCount
 * @param {string?} props.currentPage
 */
const MobileBottomNav = props => {
  const {
    isAuthenticated,
    inboxTab = 'orders',
    createListingType,
    notificationCount = 0,
    currentPage,
  } = props;
  const isInApp = useIsInTwinrApp();
  const isNarrow = useIsNarrowViewport();
  const intl = useIntl();
  const preview = isPreviewMobileBottomNav();
  const allowed = shouldShowMobileBottomNav({
    isAuthenticated,
    isInApp,
  });
  const show = preview || (allowed && isNarrow);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }
    document.body.classList.toggle(BODY_CLASS, show);
    document.body.classList.toggle(BODY_PREVIEW_CLASS, show && preview);
    document.body.classList.toggle(BODY_ANDROID_CLASS, show && isAndroid());
    return () => {
      document.body.classList.remove(BODY_CLASS);
      document.body.classList.remove(BODY_PREVIEW_CLASS);
      document.body.classList.remove(BODY_ANDROID_CLASS);
    };
  }, [show]);

  if (!show || typeof document === 'undefined') {
    return null;
  }

  const homeActive = isPage(currentPage, 'LandingPage');
  const publishActive =
    isPage(currentPage, 'EditListingPage') || isPage(currentPage, 'NewListingPage');
  const chatActive = isPage(currentPage, 'InboxPage');
  const listingsActive = isPage(currentPage, 'ManageListingsPage');
  const profileActive =
    isPage(currentPage, 'ProfileSettingsPage') ||
    isPage(currentPage, 'ProfilePage') ||
    ACCOUNT_SETTINGS_PAGES.includes(currentPage);

  const publishTo = createListingType ? { search: `?listingType=${createListingType}` } : undefined;

  return createPortal(
    <nav
      className={classNames(css.root, { [css.rootPreview]: preview })}
      aria-label={intl.formatMessage({ id: 'MobileBottomNav.ariaLabel' })}
    >
      <NamedLink
        name="LandingPage"
        className={classNames(css.item, { [css.itemActive]: homeActive })}
      >
        <HomeIcon filled={homeActive} />
        <span className={css.label}>
          <FormattedMessage id="MobileBottomNav.home" />
        </span>
      </NamedLink>
      <NamedLink
        name="EditListingPage"
        params={{
          slug: 'draft',
          id: '00000000-0000-0000-0000-000000000000',
          type: 'new',
          tab: 'details',
        }}
        to={publishTo}
        className={classNames(css.item, { [css.itemActive]: publishActive })}
      >
        <PublishIcon filled={publishActive} />
        <span className={css.label}>
          <FormattedMessage id="MobileBottomNav.publish" />
        </span>
      </NamedLink>
      <NamedLink
        name="InboxPage"
        params={{ tab: inboxTab }}
        className={classNames(css.item, { [css.itemActive]: chatActive })}
      >
        <span className={css.iconWrap}>
          <ChatIcon filled={chatActive} />
          {notificationCount > 0 ? <span className={css.dot} /> : null}
        </span>
        <span className={css.label}>
          <FormattedMessage id="MobileBottomNav.chat" />
        </span>
      </NamedLink>
      <NamedLink
        name="ManageListingsPage"
        className={classNames(css.item, { [css.itemActive]: listingsActive })}
      >
        <ListingsIcon filled={listingsActive} />
        <span className={css.label}>
          <FormattedMessage id="MobileBottomNav.listings" />
        </span>
      </NamedLink>
      <NamedLink
        name="ProfileSettingsPage"
        className={classNames(css.item, { [css.itemActive]: profileActive })}
      >
        <ProfileIcon filled={profileActive} />
        <span className={css.label}>
          <FormattedMessage id="MobileBottomNav.profile" />
        </span>
      </NamedLink>
    </nav>,
    document.body
  );
};

export default MobileBottomNav;
